import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import * as noteService from '../../service/note.service';
import {
    Note,
    NoteCreateRequest,
    NoteSearchCriteria,
    NoteSummary,
    NoteUpdateRequest,
    getDefaultNoteSearchCriteria,
} from '../../service/model/note.model';
import { showNotification } from '../alerts/alert.slice';
import { NotificationType } from '../../shared/enums';
import { getAxiosErrorMessage } from '../../service/axios/axios.call.service';
import { PaginatedResponse } from '../../service/model/paginated-response';

export interface NoteState {
    isLoading: boolean;
    notes: Note[];
    summary?: NoteSummary;
    page?: PaginatedResponse<Note>;
    totalPages: number;
    currentPage: number;
    totalRecords: number;
}

const noteAdapter = createEntityAdapter({
    selectId: (note: Note) => note.id,
});

export const initialState = noteAdapter.getInitialState({
    isLoading: false,
    notes: [],
    totalPages: 0,
    currentPage: 0,
    totalRecords: 0,
} as NoteState);

export const fetchNoteSummary = createAsyncThunk<NoteSummary>(
    'notes/summary',
    async (_, { rejectWithValue }) => {
        return noteService.getNoteSummary().catch((errors) => {
            return rejectWithValue(errors);
        });
    }
);

export const fetchNotesByBusiness = createAsyncThunk<
    PaginatedResponse<Note>,
    NoteSearchCriteria
>('notes/business', async (request, { rejectWithValue }) => {
    return noteService
        .getNotesByBusinessId(request.pagination)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const fetchNotesByMember = createAsyncThunk<
    PaginatedResponse<Note>,
    NoteSearchCriteria
>('notes/member', async (request, { rejectWithValue }) => {
    if (!request.targetId) {
        return rejectWithValue('Member ID is required');
    }
    return noteService
        .getNotesByMemberId(request.targetId, request.pagination)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const fetchNotesByUser = createAsyncThunk<
    PaginatedResponse<Note>,
    NoteSearchCriteria
>('notes/user', async (request, { rejectWithValue }) => {
    if (!request.targetId) {
        return rejectWithValue('User ID is required');
    }
    return noteService
        .getNotesByUserId(request.targetId, request.pagination)
        .catch((errors) => {
            return rejectWithValue(errors);
        });
});

export const createNote = createAsyncThunk<Note, NoteCreateRequest>(
    'notes/create',
    async (request, { rejectWithValue, dispatch }) => {
        return noteService
            .createNote(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Note created successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchNotesByBusiness(getDefaultNoteSearchCriteria()));
                dispatch(fetchNoteSummary());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const updateNote = createAsyncThunk<Note, NoteUpdateRequest>(
    'notes/update',
    async (request, { rejectWithValue, dispatch }) => {
        return noteService
            .updateNote(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Note updated successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchNotesByBusiness(getDefaultNoteSearchCriteria()));
                dispatch(fetchNoteSummary());
                // dispatch(fetchMembers());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

export const deleteNote = createAsyncThunk<number, number>(
    'notes/delete',
    async (request, { rejectWithValue, dispatch }) => {
        return noteService
            .deleteNote(request)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Note deleted successfully',
                        type: NotificationType.Success,
                    })
                );
                dispatch(fetchNotesByBusiness(getDefaultNoteSearchCriteria()));
                dispatch(fetchNoteSummary());
                // dispatch(fetchMembers());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    })
                );
                return rejectWithValue(errors);
            });
    }
);

const noteSlice = createSlice({
    name: 'note',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchNoteSummary.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchNoteSummary.fulfilled,
                (state, action: PayloadAction<NoteSummary>) => {
                    state.summary = action.payload;
                    state.isLoading = false;
                }
            )
            .addCase(fetchNoteSummary.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchNotesByBusiness.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchNotesByBusiness.fulfilled,
                (state, action: PayloadAction<PaginatedResponse<Note>>) => {
                    noteAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                }
            )
            .addCase(fetchNotesByBusiness.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchNotesByMember.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchNotesByMember.fulfilled,
                (state, action: PayloadAction<PaginatedResponse<Note>>) => {
                    noteAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                }
            )
            .addCase(fetchNotesByMember.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(fetchNotesByUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchNotesByUser.fulfilled,
                (state, action: PayloadAction<PaginatedResponse<Note>>) => {
                    noteAdapter.setAll(state, action.payload.data);
                    state.currentPage = action.payload.currentPage;
                    state.totalPages = action.payload.totalPages;
                    state.totalRecords = action.payload.totalRecords;
                    state.isLoading = false;
                }
            )
            .addCase(fetchNotesByUser.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createNote.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createNote.fulfilled,
                (state, action: PayloadAction<Note>) => {
                    noteAdapter.addOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(createNote.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateNote.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateNote.fulfilled,
                (state, action: PayloadAction<Note>) => {
                    noteAdapter.upsertOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(updateNote.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteNote.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteNote.fulfilled,
                (state, action: PayloadAction<number>) => {
                    noteAdapter.removeOne(state, action.payload);
                    state.isLoading = false;
                }
            )
            .addCase(deleteNote.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const { selectAll: selectNotes, selectById: selectNoteById } =
    noteAdapter.getSelectors<RootState>((state) => state.note);

export const selectIsLoading = (state: RootState) => state.note.isLoading;
export const selectNoteSummary = (state: RootState) => state.note.summary;
export const selectCurrentPage = (state: RootState) => state.note.currentPage;
export const selectTotalPages = (state: RootState) => state.note.totalPages;
export const selectTotalRecords = (state: RootState) => state.note.totalRecords;

export default noteSlice.reducer;
